/**
 * MFA API Client
 *
 * API functions for MFA verification flow.
 * Uses AuthenticatedApiClient (Cognito Bearer token).
 */

import {AuthenticatedApiClient} from '@matic/lib/api/authenticated-api-client';

class MfaApiClient extends AuthenticatedApiClient {
  private get basePath() {
    return '/api/v1/identity';
  }

  async fetchTenantSettings(): Promise<{
    mfaRequired: boolean;
    cuiEnabled: boolean;
    qmsRecordsEnabled: boolean;
  }> {
    const response = await this.get<{
      mfa_required: boolean; // eslint-disable-line @typescript-eslint/naming-convention
      cui_enabled: boolean; // eslint-disable-line @typescript-eslint/naming-convention
      qms_records_enabled: boolean; // eslint-disable-line @typescript-eslint/naming-convention
    }>(`${this.basePath}/tenant-settings`);
    return {
      mfaRequired: response.mfa_required,
      cuiEnabled: response.cui_enabled,
      qmsRecordsEnabled: response.qms_records_enabled,
    };
  }

  async sendMfaCode(): Promise<void> {
    await this.post(`${this.basePath}/mfa/send-code`, {});
  }

  async verifyMfaCode(
    code: string
  ): Promise<{verified: boolean; mfaToken: string}> {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const response = await this.post<{verified: boolean; mfa_token: string}>(
      `${this.basePath}/mfa/verify-code`,
      {code}
    );
    return {verified: response.verified, mfaToken: response.mfa_token};
  }

  async validateMfaToken(mfaToken: string): Promise<{valid: boolean}> {
    return this.post<{valid: boolean}>(
      `${this.basePath}/mfa/validate-token`,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      {mfa_token: mfaToken}
    );
  }
}

// Singleton
const mfaApi = new MfaApiClient();

export function getMfaApi(token?: string): MfaApiClient {
  if (token) {
    return new MfaApiClient(token);
  }

  return mfaApi;
}

export {mfaApi};
